import React, { useState } from 'react';
import { Box, Typography, Button, Rating, TextField } from '@mui/material';
import googleIcon from '../../../../assets/images/google.png';
import trustIcon from '../../../../assets/images/trustpilot.png';
import { useHandleSubmit } from './hooks';
import { useLocation } from 'react-router-dom';
import { surveyPortalMixpanel } from '../../../../vendor/mixpanel';

const PaymentPending = () => {
  const [rating, setRating] = useState<number | undefined>(undefined);
  const [ratingSubmitted, setRatingSubmitted] = useState<boolean>(false);
  const handleSubmit = useHandleSubmit();
  const [comment, setComment] = useState('');
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const SEID = queryParams.get('SEID') ?? queryParams.get('seid') ?? '';
  const email = queryParams.get('email') ?? queryParams.get('email_address') ?? '';
  const surveyId = queryParams.get('survey_id');
  const source = queryParams.get('survey_host');
  const onSubmitRating = () => {
    const feedbackData = { rating: rating ?? 1, respondent_id: SEID, comment: comment };
    handleSubmit(feedbackData);
    setRatingSubmitted(true);
  };
  const hasTrackedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasTrackedRef.current && SEID) {
      surveyPortalMixpanel.track('Getting Paid Page Viewed', {
        SSEID: SEID,
        survey_id: surveyId,
        survey_host: source,
      });
      hasTrackedRef.current = true;
    }
  }, [SEID]);

  const hasTrackedRefRating = React.useRef(false);
  React.useEffect(() => {
    if (!hasTrackedRefRating.current && rating && rating >= 4) {
      surveyPortalMixpanel.track('Leave Public Review Section Shown', {
        SSEID: SEID,
        survey_id: surveyId,
        survey_host: source,
        source: 'Survey',
        rating_given: rating,
        button_location: "You're Getting Paid page",
      });
      hasTrackedRefRating.current = true;
    }
  }, [rating]);
  const trackGoogleReview = () => {
    surveyPortalMixpanel.track('Review on Google Button Clicked', {
      SSEID: SEID,
      survey_id: surveyId,
      survey_host: source,
      source: 'Survey',
      rating_given: rating,
      button_location: "You're Getting Paid page",
    });
  };
  const trackTrustPilotReview = () => {
    surveyPortalMixpanel.track('Review on Trustpilot Button Clicked', {
      SSEID: SEID,
      survey_id: surveyId,
      survey_host: source,
      source: 'Survey',
      rating_given: rating,
      button_location: "You're Getting Paid page",
    });
  };

  return (
    <Box maxWidth="480px">
      <Typography variant={'h2'} marginBottom="16px" fontWeight={700}>
        You’re getting paid!
      </Typography>
      <Typography variant={'body1'} color={'text.secondary'} marginBottom="16px">
        You’ll receive a link via email to claim your reward from our payment partner, Tremendous.
        The email was sent to:
      </Typography>
      <Typography variant={'body1'} color={'text.secondary'} marginBottom="16px" fontWeight={700}>
        {email}
      </Typography>
      <Typography variant={'body1'} color={'text.secondary'} marginBottom="32px">
        You can reach out to us with any questions or issues at payments@newtonx.com.
      </Typography>
      {!ratingSubmitted && (
        <>
          <Typography variant={'h3'} marginBottom="16px" fontWeight={700}>
            How was your experience?
          </Typography>
          <Typography variant={'body1'} color={'text.secondary'} marginBottom="16px">
            We use this feedback to create a better survey experience.
          </Typography>
          <Rating
            name="rating-value"
            value={rating}
            onChange={(_, newValue) => {
              if (newValue && newValue >= 1) {
                setRating(newValue);
              }
            }}
            size="large"
            sx={{
              width: '100%',
              fontSize: '2.8rem', // increase star size
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '24px',
            }}
          />

          {rating && rating < 4 && (
            <>
              <TextField
                sx={{
                  marginBottom: '4px',
                }}
                label="Feedback (optional)"
                multiline
                minRows={4}
                value={comment}
                fullWidth
                variant="outlined"
                inputProps={{ maxLength: 500 }}
                onChange={(e) => setComment(e.target.value)}
              />
              <Typography
                variant="caption"
                color={comment.length >= 500 ? 'error' : 'text.secondary'}
                sx={{ marginBottom: '16px', display: 'block', textAlign: 'right' }}
              >
                {comment.length}/500 characters
              </Typography>
            </>
          )}
          {rating && (
            <Button color="primary" fullWidth variant="outlined" onClick={onSubmitRating}>
              SUBMIT
            </Button>
          )}
        </>
      )}
      {ratingSubmitted && rating && (
        <>
          {rating < 4 && (
            <Box>
              <Typography variant={'h3'} marginBottom="16px" fontWeight={700}>
                Feedback submitted
              </Typography>
              <Typography variant={'body1'} color={'text.secondary'} marginBottom="16px">
                Thank you! We value your feedback and will use it to create a better survey
                experience.
              </Typography>
            </Box>
          )}
          {rating >= 4 && (
            <Box>
              <Typography variant={'h3'} marginBottom="16px" fontWeight={700}>
                Thanks for your rating!
              </Typography>
              <Typography variant={'body1'} color={'text.secondary'} marginBottom="16px">
                Let others know about your experience with NewtonX by leaving us a public review.
              </Typography>
              <Button
                color="primary"
                fullWidth
                variant="outlined"
                sx={{ mb: '16px' }}
                component="a"
                href="https://g.page/r/CX_SiHIANKOMEAE/review"
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackGoogleReview}
              >
                <Box
                  component={'img'}
                  sx={{
                    width: '24px',
                    height: '24px',
                    marginRight: '8px',
                  }}
                  alt="google"
                  src={googleIcon}
                ></Box>
                REVIEW ON GOOGLE
              </Button>
              <Button
                color="primary"
                fullWidth
                variant="outlined"
                sx={{ mb: '16px' }}
                component="a"
                href="https://www.trustpilot.com/evaluate/newtonx.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackTrustPilotReview}
              >
                <Box
                  component={'img'}
                  sx={{
                    width: '24px',
                    height: '23px',
                    marginRight: '8px',
                  }}
                  alt="trustpilot"
                  src={trustIcon}
                ></Box>
                REVIEW ON TRUSTPILOT
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PaymentPending;
